import { Loading, useDataMutation } from "@lyfegen-platform/react-common";
import { Box, IconButton, Paper, Typography } from "@material-ui/core";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { ErrorCode, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { Upload } from "../../../components/icons/Upload";
import { UPDATE_PROFILE_PICTURE_OR_SIGNATURE } from "../queries";
import ImageEdit, { ImageType } from "./ImageEdit";
import usePersonalInfo from "./use-personal-info";
import useStyles from "../../data/forms/forms-styles";
import { Area } from "react-easy-crop";
import LyfeButton from "libs/react-common/src/Button/LyfeButton";
import getCroppedImg from "./getCroppedImg";
import { getBase64 } from "../../../utils/base64";
import { useSetUserFromUserDataProps } from "../../../store/user";
import { BlobContainerType } from "../../../models/generated/graphql";
import { Delete } from "@material-ui/icons";
import Resizer from "react-image-file-resizer";

interface ImageUploadProps {
	isImagePresent?: boolean;
	areButtonsDisabled?: boolean;
	imageType: ImageType;
	setIsImagePresent: (state: boolean) => void;
	onDiscardClick: () => any;
	setSaveEnabled?: (enabled: boolean) => void;
	paperStyles?: any;
}

const resizeFile = (file: any) =>
	new Promise((resolve) => {
		Resizer.imageFileResizer(
			file,
			460,
			300,
			file.type || "JPEG",
			100,
			0,
			(uri) => {
				resolve(uri);
			},
			"file"
		);
	});

const onResize = async (dropzoneFile: any) => {
	try {
		return await resizeFile(dropzoneFile);
	} catch (err) {
		console.log(err);
		return dropzoneFile;
	}
};

const ImageUpload = forwardRef((props: ImageUploadProps, ref) => {
	const [personalInfo] = usePersonalInfo();
	const defineSourceImage = () => {
		if (props.imageType === ImageType.AVATAR) {
			return personalInfo.sourceUserImage;
		}
		if (!personalInfo.isSignatureCreationDraw) {
			return personalInfo.sourceUserSignature;
		}

		return undefined;
	};
	const sourceImage = defineSourceImage();
	const blobContainerType = props.imageType === ImageType.AVATAR ? BlobContainerType.ProfilePicture : BlobContainerType.Signature;
	const [path, setPath] = useState(sourceImage);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const setUser = useSetUserFromUserDataProps();
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);
	const classes = useStyles();

	const [handleSave, { loading }] = useDataMutation(UPDATE_PROFILE_PICTURE_OR_SIGNATURE);
	const { t } = useTranslation();

	const onDrop = useCallback(
		async (acceptedFiles) => {
			if (acceptedFiles.length === 1) {
				const resizedFile =
					props.imageType === ImageType.SIGNATURE ? ((await onResize(acceptedFiles[0])) as File) : acceptedFiles[0];
				setPath(URL.createObjectURL(resizedFile));
				props.setIsImagePresent(true);
				setUploadedFile(resizedFile);
			}
			props.setSaveEnabled && props.setSaveEnabled(true);
		},
		[setPath, setUploadedFile]
	);

	useImperativeHandle(
		ref,
		() => ({
			async saveImage() {
				await onSave();
			},
			deleteImage() {
				onDelete();
			},
		}),
		[path, croppedAreaPixels]
	);

	const onSave = async () => {
		const blobName = props.imageType === ImageType.AVATAR ? personalInfo.id : personalInfo.id + "-signature";
		const blobNameSource = props.imageType === ImageType.AVATAR ? personalInfo.id + "-source" : personalInfo.id + "-signature-source";
		if (croppedAreaPixels && props.isImagePresent && path?.length) {
			const cropImageUrl = await getCroppedImg(path, croppedAreaPixels);
			const fullImage = uploadedFile && (await getBase64(uploadedFile));
			if (cropImageUrl) {
				handleSave({
					variables: {
						userProfileOrSignatureInput: {
							blobContainerType: blobContainerType,
							blobName: blobName,
							content: cropImageUrl,
							blobNameSource: blobNameSource,
							contentSource: fullImage || sourceImage,
						},
					},
				});

				setUser({
					...personalInfo,
					...(props.imageType === ImageType.AVATAR && {
						userImg: cropImageUrl,
						sourceUserImage: fullImage || sourceImage,
					}),
					...(props.imageType === ImageType.SIGNATURE && {
						userSignature: cropImageUrl,
						sourceUserSignature: fullImage || sourceImage,
						isSignatureCreationDraw: false,
					}),
					questsCount: 0,
				});
			}

			props.setIsImagePresent(true);
		}

		if (!props.isImagePresent) {
			handleSave({
				variables: {
					userProfileOrSignatureInput: {
						blobContainerType: blobContainerType,
						blobName: blobName,
						blobNameSource: blobNameSource,
					},
				},
			});
			setUser({
				...personalInfo,
				...(props.imageType === ImageType.AVATAR && {
					userImg: null,
					sourceUserImage: null,
				}),
				...(props.imageType === ImageType.SIGNATURE && {
					userSignature: null,
					sourceUserSignature: null,
				}),
				questsCount: 0,
			});
		}
	};

	const onDelete = () => {
		props.setIsImagePresent(false);
		setPath(null);
		setUploadedFile(null);
		props.setSaveEnabled && props.setSaveEnabled(false);
	};

	const { getRootProps, getInputProps, open, fileRejections } = useDropzone({
		maxFiles: 1,
		noClick: true,
		noKeyboard: true,
		accept: [".jpeg", ".png", ".jpg"],
		maxSize: props.imageType === ImageType.AVATAR ? 10000000 : 2000000,
		onDrop,
	});

	const onCropComplete = useCallback((_croppedArea: Area, croppedAreaRange) => {
		setCroppedAreaPixels(croppedAreaRange);
	}, []);

	return (
		<Box>
			{!!props.isImagePresent && <Typography className={classes.cropMessage}>{t("crop-image")}</Typography>}
			<Paper style={{ ...props.paperStyles }} className={classes.uploadContainer} {...getRootProps()}>
				{loading && <Loading />}
				{!props.isImagePresent && !loading && (
					<>
						<Upload />
						<Box>
							<Typography display="inline">{`${t("drag-n-drop")}, ${t("or")}`}</Typography>
							<Typography display="inline" color="primary" style={{ cursor: "pointer" }} onClick={open}>
								&nbsp;{t("browse")}
							</Typography>
						</Box>
						<Typography style={{ color: "#969392" }}>
							{props.imageType === ImageType.AVATAR ? t("image-format-size-options") : t("signature-format-size-options")}
						</Typography>
						<input {...getInputProps()} name="avatar" />
					</>
				)}
				{(!!sourceImage || path) && props.isImagePresent && !loading && (
					<Box style={{ width: "100%", backgroundColor: "pink" }}>
						<ImageEdit
							imageType={props.imageType}
							path={path ? path : sourceImage!}
							onCropComplete={onCropComplete}
							hasCropChanged={props.setSaveEnabled}
						/>
						{props.imageType === ImageType.SIGNATURE && (
							<IconButton style={{ position: "absolute", right: "10px", top: "10px" }} onClick={onDelete} color="primary">
								<Delete fontSize="small" />
							</IconButton>
						)}
					</Box>
				)}
				{fileRejections &&
					fileRejections.map((r, i) => (
						<Box display="flex" width="100%" justifyContent="center" alignItems="center" marginTop={2} key={i}>
							{r.errors.map((e, idx) => (
								<Typography key={idx} color="error" variant="subtitle2">
									{e.code === ErrorCode.FileInvalidType ? t("signature-error-format") : t("signature-error-size")}
								</Typography>
							))}
						</Box>
					))}
			</Paper>
			{!props.areButtonsDisabled && (
				<div className={classes.dialogButtons}>
					{props.isImagePresent && (
						<LyfeButton variant="text" color="primary" onClick={onDelete}>
							{t("delete").toUpperCase()}
						</LyfeButton>
					)}
					<LyfeButton variant="outlined" color="primary" onClick={props.onDiscardClick} style={{ margin: "0 23px" }}>
						{t("cancel").toUpperCase()}
					</LyfeButton>
					<LyfeButton variant="contained" color="primary" onClick={onSave}>
						{t("save").toUpperCase()}
					</LyfeButton>
				</div>
			)}
		</Box>
	);
});

export default ImageUpload;
